import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PaymentStatusBadge } from "@/components/PaymentStatusBadge";
import { startProcessingFeePayment } from "@/lib/payments";
import { formatINR } from "@/lib/format";
import { CreditCard, Loader2, ShieldCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

// Customer card to pay the processing fee (amount sourced from the active admin offer).
export const ProcessingFeeCard = ({ payment, onDone }) => {
  const [busy, setBusy] = useState(false);
  if (!payment || !payment.enabled || !payment.processing_fee || payment.processing_fee <= 0) return null;

  const latest = payment.latest;
  const paid = payment.paid;

  const pay = async () => {
    setBusy(true);
    try {
      const orderId = await startProcessingFeePayment();
      // Checkout modal closed — verify server-side.
      await api.get(`/payments/status/${orderId}`).catch(() => {});
      toast.message("Verifying your payment…");
      if (onDone) await onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start payment");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-5 sm:p-6 ip-card-shadow" data-testid="processing-fee-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 text-brand">
          <CreditCard className="h-5 w-5" />
          <p className="text-xs font-semibold uppercase tracking-[0.15em]">Processing Fee Payment</p>
        </div>
        {latest && <PaymentStatusBadge status={latest.status} />}
      </div>

      <p className="font-mono text-3xl font-extrabold text-brand mt-3" data-testid="processing-fee-amount">{formatINR(payment.processing_fee)}</p>
      <p className="text-xs text-muted-foreground mt-1">Payable processing fee set by Instant Paisa for your approved offer.</p>

      {paid ? (
        <div className="mt-4 inline-flex items-center gap-2 text-emerald-700 font-semibold text-sm" data-testid="processing-fee-paid">
          <CheckCircle2 className="h-5 w-5" /> Processing fee paid
          {latest?.payment_reference && <span className="font-mono text-muted-foreground font-normal">· Ref {latest.payment_reference}</span>}
        </div>
      ) : (
        <>
          <Button onClick={pay} disabled={busy} className="mt-4 w-full sm:w-auto h-11 bg-gold hover:bg-gold-dark text-white" data-testid="pay-processing-fee-button">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CreditCard className="h-4 w-4 mr-2" />} Pay Processing Fee
          </Button>
          <div className="mt-3 flex gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="h-4 w-4 shrink-0 text-brand" />
            <p>Payments are processed securely via our certified payment gateway. Your status updates only after server-side verification.</p>
          </div>
        </>
      )}
    </Card>
  );
};
